import { ChangeDetectionStrategy, Component, inject, signal, ChangeDetectorRef } from '@angular/core';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ApiService } from './services/api.service';
import { ModalSpinner } from './shared/ui/modal-spinner';
import { ModalGeneric } from './shared/ui/modal-generic';
import { AppButton } from './shared/ui/app-button';
import { TranslateService, TranslatePipe, TranslateDirective } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, interval, startWith, tap, map, delay, merge, switchMap, Observable } from 'rxjs';
import { RenameService } from './services/rename.service';

// classe rjxs auxiliar per reiniciar
// els missatges del "loading"
class ResettableInterval {
  private resetTrigger = new Subject<void>();
  count$: Observable<number>;
  constructor(private intervalMs: number = 1000) {
    this.count$ = this.resetTrigger.pipe(
      switchMap(()=>interval(this.intervalMs).pipe(startWith(0))),
    );
    this.resetTrigger.next();
  }
  reset(): void {
    this.resetTrigger.next();
  }
}

@Component({
  selector: 'app-root',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterOutlet, TranslatePipe, TranslateDirective, ModalSpinner, AppButton, CommonModule, ReactiveFormsModule, ModalGeneric],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App {
  private translate = inject(TranslateService);
  private api = inject(ApiService);
  private cdr = inject(ChangeDetectorRef);
  public readonly renamer = inject(RenameService);
  protected readonly title = signal('previn-ia-app');
  private loading_messages!:string[];

  nameControl = new FormControl<string>('');
  
  // Observable que controla quan mostrar el modal 'Loading...':
  protected readonly busy = this.api.busy$.pipe(
    takeUntilDestroyed(),
    // evita que el cicle del "change-detection" de l'angular 
    // falli si 'busy' canvia d'estat molt ràpidament
    tap((b)=>{
      if (!!b) this._messages.reset();
      queueMicrotask(()=>this.cdr.detectChanges());
    })
  );
  private readonly _dismissError = new Subject<null>();
  protected readonly error$ = merge(
    this.api.error$.pipe(takeUntilDestroyed()),
    this.api.error$.pipe(takeUntilDestroyed(), delay(5000), map(e=>null)),
    this._dismissError.pipe(takeUntilDestroyed())
  );

  // // Observable que proporciona missatges per al mateix:
  // protected messages = new Observable<string>(observer=>{
  //   const _sub = interval(4500).pipe(
  //     map(n=>LOADING_MESSAGES[ n % LOADING_MESSAGES.length ])
  //   ).subscribe(observer);
  //   return ()=>_sub.unsubscribe()
  // });

  private readonly _messages = new ResettableInterval(4500);
  protected readonly messages = this._messages.count$.pipe(
    startWith(0),
    map(n=>this.loading_messages[ n % this.loading_messages.length ])
  );
  
  constructor() {
    this.translate.addLangs(['ca','es','en']);
    this.translate.setFallbackLang('ca');
    this.translate.use('ca');
    this.loading_messages = this.translate.instant('loading_messages');
    this.translate.onLangChange.pipe(
      takeUntilDestroyed(),
    ).subscribe({
      next: ()=>{
        this.loading_messages = this.translate.instant('loading_messages');
      }
    })
  }

  requestRename(name:string) {
    const {assessment_id, company_id, risk_source_type_id, risk_source_id } = this.renamer.showRenameModal()!
    this.nameControl.setValue('');
    this.renamer.showRenameModal.set(undefined);
    this.api.renameRiskSource(assessment_id, company_id, risk_source_type_id, risk_source_id, name).pipe(
      this.api.busyWatch()
    ).subscribe({
      next: (resp)=>this.renamer.renamedTo(name)
    })
  }

  dismissError() {
    this._dismissError.next(null);
  }
}
